import type { BootServices } from '../bootstrap';
import { AppError } from '../monitoring/appError';

const JWT_LIKE_RE = /\beyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9._-]{10,}\.[a-zA-Z0-9._-]{10,}\b/g
function scrubSnippet(x: any): string {
  const s = String(x || '')
  if (!s) return ''
  // Basic redactions (best-effort; backend scrubs again).
  return s
    .replace(JWT_LIKE_RE, '<jwt>')
    .replace(/([?&])(token|auth|key|signature|hash)=[^&#\s]+/gi, '$1$2=<redacted>')
}

async function safeReadBodySnippet(res: Response): Promise<{ content_type: string | null; body_snippet: string | null }> {
  try {
    const ct = String(res.headers?.get?.('content-type') || '').slice(0, 120) || null
    // PII guard: only read snippet for text payloads (mostly nginx/html error pages).
    const allow =
      !ct
        ? false
        : (ct.includes('text/html') || ct.startsWith('text/') || ct.includes('application/xhtml+xml'))
    if (!allow) return { content_type: ct, body_snippet: null }
    const txt = await res.text().catch(() => '')
    const sn = scrubSnippet(String(txt || '').replace(/\s+/g, ' ').trim().slice(0, 220))
    return { content_type: ct, body_snippet: sn || null }
  } catch {
    return { content_type: null, body_snippet: null }
  }
}

function withDefaultOmit(init?: RequestInit): RequestInit | undefined {
  // Mini-app architecture: do not rely on cookies for auth; always prefer Bearer tokens.
  // Default fetch() credentials is "same-origin" which will send cookies to aicams.co.
  // That can cause intermittent nginx 400 for media/range requests if cookies are large.
  // So we default to omit unless caller explicitly sets credentials.
  if (!init) return { credentials: 'omit' };
  if (init.credentials) return init;
  return { ...init, credentials: 'omit' };
}

/**
 * apiFetch:
 * - единая низкоуровневая точка для fetch() по API/JSON/стримам
 * - всегда по умолчанию credentials:'omit' (если не переопределено явно)
 *
 * Важно: бизнес-ошибки/HTTP-ошибки нужно оборачивать через apiJsonOrThrow/authedApiJsonOrThrow,
 * но для стримов/нестандартных ответов (SSE/stream/plain) можно использовать apiFetch напрямую.
 */
export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  return await fetch(url, withDefaultOmit(init))
}

export async function getAccessTokenOrNull(services: BootServices, opts?: { timeoutMs?: number }): Promise<string | null> {
  const hasJwt = await services.supabase.waitForJwt({ timeoutMs: opts?.timeoutMs ?? 3000 });
  if (!hasJwt) return null;
  return services.supabase.getAccessToken();
}

export async function fetchJson<T = any>(url: string, init?: RequestInit): Promise<{ ok: true; status: number; data: T } | { ok: false; status: number; data: any }> {
  const res = await apiFetch(url, init);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, status: res.status, data };
  return { ok: true, status: res.status, data };
}

type ApiOk<T> = { ok: true } & T
type ApiFail = { ok?: false; error?: string; message?: string }

async function safeReadJson(res: Response): Promise<any> {
  try {
    return await res.json()
  } catch {
    return null
  }
}

/**
 * apiJsonOrThrow:
 * - единая точка для JSON API вызовов
 * - кидает AppError на transport (HTTP) или на api-level ok:false
 * - НЕ использует telemetry напрямую (это делает UI error-sink на границе UI)
 */
export async function apiJsonOrThrow<T = any>(url: string, init?: RequestInit, ctx?: Record<string, any>): Promise<ApiOk<T>> {
  const method = String((init as any)?.method || 'GET').toUpperCase()
  const isGet = method === 'GET'
  const attempts = isGet ? 2 : 1

  let lastErr: any = null
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const res = await apiFetch(url, init)
    const data = await safeReadJson(res)
    if (!res.ok) {
      const extra = await safeReadBodySnippet(res)

      // IMPORTANT:
      // We have observed rare transient HTTP/2 -> 400 "empty" responses (no JSON/body), especially in WebViews.
      // For idempotent GET requests only, do ONE retry when:
      // - status=400
      // - response body is not JSON (data=null)
      // - content-type is missing or not json (extra.content_type not json-like)
      const ct = String(extra?.content_type || '')
      const looksJsonCt = ct.includes('application/json') || ct.includes('+json') || ct.includes('text/json')
      const shouldRetryEmpty400 = isGet && attempt < attempts && Number(res.status || 0) === 400 && data === null && !looksJsonCt
      if (shouldRetryEmpty400) {
        lastErr = new AppError({
          kind: 'transport',
          code: `http_${Number(res.status || 0) || 0}`,
          message: `HTTP ${res.status} for ${url} (retrying once)`,
          status: res.status,
          context: { url, ...(ctx || {}), resp: data, ...extra, attempt },
          cause: data,
        })
        await new Promise((r) => setTimeout(r, 150))
        continue
      }

      // If backend returned structured JSON error, classify as API (business) error rather than transport.
      // This prevents "HTTP 400" from hiding real codes like "not_subscribed".
      if (data && typeof data === 'object') {
        const hasOkFalse = ('ok' in (data as any)) && ((data as any).ok === false)
        const hasErr = typeof (data as any).error === 'string' && String((data as any).error || '').trim()
        const hasMsg = typeof (data as any).message === 'string' && String((data as any).message || '').trim()
        if (hasOkFalse || hasErr || hasMsg) {
          const errCode = String(((data as any).error || (data as any).code || 'api_error')).slice(0, 120)
          const msg = String(((data as any).message || (data as any).error || errCode || 'API error')).slice(0, 400)
          throw new AppError({
            kind: 'api',
            code: errCode || 'api_error',
            message: msg,
            status: res.status,
            context: { url, ...(ctx || {}), resp: data, ...extra, attempt },
            cause: data,
          })
        }
      }

      throw new AppError({
        kind: 'transport',
        code: `http_${Number(res.status || 0) || 0}`,
        message: `HTTP ${res.status} for ${url}`,
        status: res.status,
        context: { url, ...(ctx || {}), resp: data, ...extra, attempt },
        cause: data,
      })
    }
    // success: keep existing behavior below
    // Convention: many endpoints return { ok: true/false, ... }
    if (data && typeof data === 'object' && 'ok' in data && (data as any).ok === false) {
      const errCode = String((data as ApiFail)?.error || 'api_error').slice(0, 120)
      const msg = String((data as ApiFail)?.message || errCode || 'API error').slice(0, 400)
      throw new AppError({
        kind: 'api',
        code: errCode || 'api_error',
        message: msg,
        status: res.status,
        context: { url, ...(ctx || {}), resp: data, attempt },
        cause: data,
      })
    }
    return (data || { ok: true }) as any
  }

  if (lastErr) throw lastErr
  // Convention: many endpoints return { ok: true/false, ... }
  throw new AppError({
    kind: 'transport',
    code: 'unknown_transport',
    message: `HTTP request failed for ${url}`,
    status: 0,
    context: { url, ...(ctx || {}) },
  })
}

/**
 * authedApiJsonOrThrow:
 * - добавляет Bearer JWT
 * - ждёт JWT ограниченное время
 */
export async function authedApiJsonOrThrow<T = any>(
  services: BootServices,
  path: string,
  init?: RequestInit,
  opts?: { timeoutMs?: number; ctx?: Record<string, any> },
): Promise<ApiOk<T>> {
  const hasJwt = await services.supabase.waitForJwt({ timeoutMs: opts?.timeoutMs ?? 5000 })
  if (!hasJwt) {
    throw new AppError({
      kind: 'auth',
      code: 'auth_not_ready',
      message: 'Auth not ready yet',
      status: 401,
      context: { path, ...(opts?.ctx || {}) },
    })
  }
  const token = services.supabase.getAccessToken()
  if (!token) {
    throw new AppError({
      kind: 'auth',
      code: 'auth_required',
      message: 'Auth required',
      status: 401,
      context: { path, ...(opts?.ctx || {}) },
    })
  }
  const apiBaseUrl = services.config.config.apiBaseUrl || ''
  return await apiJsonOrThrow<T>(`${apiBaseUrl}${path}`, {
    ...(init || {}),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  }, { path, ...(opts?.ctx || {}) })
}

/**
 * authedFetchJson:
 * - adds Bearer JWT (same as authedApiJsonOrThrow)
 * - DOES NOT throw on api-level `{ ok: false }` payloads (business outcomes)
 * - only throws on auth-not-ready / auth-required (so callers can rely on identity)
 *
 * Useful for flows where `{ ok:false, reason: ... }` is a normal UX branch (e.g., tasks not_subscribed).
 */
export async function authedFetchJson<T = any>(
  services: BootServices,
  path: string,
  init?: RequestInit,
  opts?: { timeoutMs?: number; ctx?: Record<string, any> },
): Promise<{ ok: true; status: number; data: T } | { ok: false; status: number; data: any }> {
  const hasJwt = await services.supabase.waitForJwt({ timeoutMs: opts?.timeoutMs ?? 5000 })
  if (!hasJwt) {
    throw new AppError({
      kind: 'auth',
      code: 'auth_not_ready',
      message: 'Auth not ready yet',
      status: 401,
      context: { path, ...(opts?.ctx || {}) },
    })
  }
  const token = services.supabase.getAccessToken()
  if (!token) {
    throw new AppError({
      kind: 'auth',
      code: 'auth_required',
      message: 'Auth required',
      status: 401,
      context: { path, ...(opts?.ctx || {}) },
    })
  }
  const apiBaseUrl = services.config.config.apiBaseUrl || ''
  const url = `${apiBaseUrl}${path}`
  return await fetchJson<T>(url, {
    ...(init || {}),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  })
}
